import type { ThemeConfig } from '@yimoka/antd';

/**
 * tenant-web 宿主统一向所有 federation remote 提供旧项目主题。
 * 业务模块只消费 token，避免各自复制主色、圆角和基础组件配置后产生视觉漂移。
 */
export const tenantWebTheme: ThemeConfig = {
  token: {
    colorPrimary: '#543BD1',
    colorInfo: '#543BD1',
    colorSuccess: '#78D153',
    colorWarning: '#FF8508',
    colorError: '#CE4E2A',
    colorTextBase: 'rgba(0,0,0,0.8)',
    colorBorder: 'rgba(0,0,0,0.08)',
    colorBgContainer: '#FFFFFF',
    sizeStep: 4,
    sizeUnit: 4,
    borderRadius: 8,
    borderRadiusSM: 6,
    borderRadiusXS: 4,
    borderRadiusLG: 16,
    wireframe: false,
    marginXXS: 4,
    marginMD: 16,
    marginLG: 24,
    marginXL: 32,
    marginXXL: 44,
    fontSize: 14,
  },
  components: {
    Anchor: {
      colorSplit: 'rgba(0,0,0,0.1)',
      colorText: 'rgba(0,0,0,0.8)',
      linkPaddingBlock: 4,
      linkPaddingInlineStart: 12,
      fontSize: 14,
    },
    Button: {
      borderColorDisabled: 'rgba(0,0,0,0.12)',
      defaultActiveBorderColor: '#543BD1',
      defaultActiveColor: '#543BD1',
      defaultBorderColor: 'rgba(0,0,0,0.1)',
      defaultColor: 'rgba(0,0,0,0.8)',
      defaultHoverBorderColor: '#4530B8',
      defaultHoverColor: '#4530B8',
      algorithm: true,
      textTextActiveColor: 'rgba(0,0,0,0.8)',
      textTextColor: 'rgba(0,0,0,0.8)',
      colorBgSolid: '#543BD1',
      colorBgSolidActive: '#4530B8',
      colorBgSolidHover: '#4530B8',
      colorBorder: 'rgba(0,0,0,0.1)',
      colorText: 'rgba(0,0,0,0.8)',
      paddingBlockSM: 2,
      paddingInline: 16,
      paddingInlineLG: 20,
      paddingInlineSM: 8,
      colorBgContainerDisabled: 'rgba(0,0,0,0.04)',
    },
    Typography: {
      titleMarginBottom: '20px',
      titleMarginTop: '16px',
      fontSizeHeading2: 20,
      fontSizeHeading1: 28,
      fontSizeHeading3: 18,
      fontSizeHeading4: 16,
      fontSizeHeading5: 14,
      paddingSM: 16,
    },
    Divider: {
      colorSplit: 'rgba(0,0,0,0.1)',
      colorText: 'rgba(0,0,0,0.8)',
      colorTextHeading: 'rgba(0,0,0,0.8)',
      fontSize: 14,
    },
    Layout: {
      bodyBg: '#FFFFFF',
      footerBg: '#FFFFFF',
      headerColor: 'rgba(0,0,0,0.8)',
      lightTriggerColor: 'rgba(0,0,0,0.8)',
      colorText: 'rgba(0,0,0,0.8)',
      headerHeight: 48,
      headerPadding: '0 16px',
    },
    Dropdown: {
      controlPaddingHorizontal: 12,
      fontSizeIcon: 12,
      fontSizeSM: 12,
    },
    Pagination: {
      colorBorder: 'rgba(0,0,0,0.1)',
      colorPrimaryBorder: 'rgba(84,59,209,0.08)',
      colorPrimaryHover: '#543BD1',
    },
    Menu: {
      itemSelectedColor: '#543BD1',
      subMenuItemSelectedColor: 'rgba(0,0,0,0.8)',
      horizontalItemSelectedColor: '#543BD1',
      horizontalItemHoverColor: '#543BD1',
      darkItemSelectedBg: '#543BD1',
      itemDisabledColor: 'rgba(0,0,0,0.25)',
      colorPrimaryBorder: 'rgba(84,59,209,0.08)',
      itemSelectedBg: 'rgba(84,59,209,0.08)',
      colorSplit: 'rgba(0,0,0,0.1)',
      colorText: 'rgba(0,0,0,0.8)',
      subMenuItemBg: 'rgba(84,59,209,0.04)',
      itemActiveBg: 'rgba(84,59,209,0.08)',
      groupTitleColor: 'rgba(0,0,0,0.4)',
      itemColor: 'rgba(0,0,0,0.8)',
      activeBarBorderWidth: 1,
      collapsedWidth: 66,
      iconMarginInlineEnd: 8,
      iconSize: 16,
      itemMarginBlock: 8,
      itemMarginInline: 0,
      controlHeightLG: 42,
      fontSize: 14,
      fontSizeLG: 16,
      horizontalItemBorderRadius: 8,
      itemBorderRadius: 8,
      itemHoverBg: 'rgba(0, 0, 0, 0.06)',
    },
    Cascader: {
      optionSelectedColor: '#543BD1',
      optionSelectedBg: 'rgba(84,59,209,0.08)',
      colorBgContainerDisabled: 'rgba(0,0,0,0.04)',
      colorBorder: 'rgba(0,0,0,0.1)',
      colorIcon: 'rgba(0,0,0,0.4)',
      colorSplit: 'rgba(0,0,0,0.1)',
      colorText: 'rgba(0,0,0,0.8)',
      colorTextDisabled: 'rgba(0,0,0,0.25)',
      controlItemBgHover: 'rgba(84,59,209,0.04)',
      controlItemWidth: 140,
      controlWidth: 200,
      dropdownHeight: 200,
      menuPadding: 4,
      fontSizeIcon: 10,
    },
    Checkbox: {
      colorTextDisabled: 'rgba(0,0,0,0.25)',
      colorBgContainerDisabled: 'rgba(0,0,0,0.04)',
      colorBorder: 'rgba(0,0,0,0.1)',
    },
    DatePicker: {
      activeBorderColor: '#543BD1',
      addonBg: 'rgba(84,59,209,0.04)',
      cellActiveWithRangeBg: 'rgba(84,59,209,0.04)',
      cellBgDisabled: 'rgba(0,0,0,0.04)',
      cellHoverBg: 'rgba(84,59,209,0.04)',
      cellHoverWithRangeBg: 'rgba(84,59,209,0.08)',
      cellRangeBorderColor: 'rgba(84,59,209,0.08)',
      hoverBorderColor: '#4530B8',
    },
    Collapse: {
      borderlessContentBg: 'rgba(84,59,209,0.04)',
      headerBg: 'rgb(255,255,255)',
      contentBg: 'rgba(84,59,209,0.04)',
      colorText: 'rgba(0,0,0,0.8)',
      colorTextDisabled: 'rgba(0,0,0,0.25)',
      colorTextHeading: 'rgba(0,0,0,0.8)',
      colorBorder: 'rgba(0,0,0,0.1)',
    },
    Descriptions: {
      contentColor: 'rgba(0,0,0,0.8)',
      labelBg: 'rgba(84,59,209,0.04)',
      extraColor: 'rgba(0,0,0,0.8)',
      labelColor: 'rgba(0,0,0,0.4)',
      titleColor: 'rgba(0,0,0,0.8)',
      colorSplit: 'rgba(0,0,0,0.1)',
      colorText: 'rgba(0,0,0,0.8)',
      colorTextSecondary: 'rgba(0,0,0,0.6)',
    },
    Empty: {
      colorTextDescription: 'rgba(0,0,0,0.4)',
    },
    Image: {
      colorBgMask: 'rgba(0,0,0,0.45)',
    },
    List: {
      colorBorder: 'rgba(0,0,0,0.1)',
      colorSplit: 'rgba(0,0,0,0.1)',
      colorText: 'rgba(0,0,0,0.8)',
      colorTextDescription: 'rgba(0,0,0,0.4)',
      colorTextDisabled: 'rgba(0,0,0,0.25)',
    },
    Popover: {
      colorText: 'rgba(0,0,0,0.8)',
      colorTextHeading: 'rgba(0,0,0,0.8)',
      boxShadowSecondary: '0 8px 24px rgba(0,0,0,0.10)',
    },
    Segmented: {
      itemActiveBg: 'rgba(84,59,209,0.04)',
      itemColor: 'rgba(0,0,0,0.6)',
      itemHoverBg: 'rgba(84,59,209,0.04)',
      itemHoverColor: '#543BD1',
      itemSelectedColor: 'rgba(0,0,0,0.8)',
      trackBg: 'rgba(84,59,209,0.04)',
      colorText: 'rgba(0,0,0,0.8)',
      colorTextDisabled: 'rgba(0,0,0,0.25)',
    },
  },
};
